import { BOSH_USLUB, yumaloq } from "./scale";

export interface ScatterPoint {
	key: string;
	x: number;
	y: number;
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	tooltip: string;
	/** Berilsa nuqta yonida yozuv chiqadi (faqat e'tiborli nuqtalar uchun). */
	label?: string;
}

export interface ScatterQuadrant {
	x: number;
	y: number;
	highlight: "top-right" | "bottom-right";
	label: string;
}

export interface ScatterPlotProps {
	points: ScatterPoint[];
	xMin: number;
	xMax: number;
	yMin: number;
	yMax: number;
	xLabel: string;
	yTicks: { value: number; label: string }[];
	quadrant?: ScatterQuadrant;
	/** Chizma maydoni balandligi, px (reja imzosidan kengaytirilgan). */
	height?: number;
	/** Nuqta yo'q bo'lganda matn (reja imzosidan kengaytirilgan). */
	empty?: string;
	/** Berilmasa — barcha nuqtalarning tooltip'lari (ko'p nuqtada sahifa qisqa xulosa bersin). */
	ariaLabel?: string;
}

/**
 * `qiymat` ni `[min, max]` dan `[0, 100]` foizga, chegaralar bilan
 * qisib. `max <= min` yoki `NaN` → 0.
 */
function joy(qiymat: number, min: number, max: number): number {
	const oraliq = max - min;

	if (!(oraliq > 0)) {
		return 0;
	}

	const foiz = ((qiymat - min) / oraliq) * 100;

	if (Number.isNaN(foiz)) {
		return 0;
	}

	return yumaloq(Math.min(100, Math.max(0, foiz)));
}

/**
 * Tarqoqlik diagrammasi (sof CSS, absolyut joylashgan nuqtalar).
 *
 * ⚠️ **Nuqtalar `<div>`, SVG `<circle>` EMAS** — spec'dagi 2 px sirt
 * halqasi `box-shadow` bilan beriladi, u esa SVG elementiga
 * qo'llanmaydi. Halqa ustma-ust tushgan nuqtalarni ajratadi.
 *
 * ⚠️ **Yorliq faqat `label` berilgan nuqtada** — hammasiga yozilsa
 * zich bulutda matn o'qib bo'lmaydi; qolganlari `title` bilan.
 *
 * ⚠️ `data-point` / `data-point-label` / `data-quadrant` /
 * `data-y-tick` — TEST KONTRAKTI.
 */
export function ScatterPlot({
	points,
	xMin,
	xMax,
	yMin,
	yMax,
	xLabel,
	yTicks,
	quadrant,
	height = 220,
	empty,
	ariaLabel,
}: ScatterPlotProps) {
	if (points.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	return (
		<div>
			<div className="flex gap-1.5">
				<div className="relative w-7 shrink-0" style={{ height: `${height}px` }}>
					{yTicks.map((belgi) => (
						<span
							key={belgi.value}
							className="absolute right-0 translate-y-1/2 text-[10px] tabular-nums"
							style={{ bottom: `${joy(belgi.value, yMin, yMax)}%`, color: "var(--chart-axis)" }}
						>
							{belgi.label}
						</span>
					))}
				</div>

				<div
					role="img"
					aria-label={ariaLabel ?? points.map((n) => n.tooltip).join(", ")}
					className="relative flex-1"
					style={{
						height: `${height}px`,
						borderLeft: "1px solid var(--chart-grid)",
						borderBottom: "1px solid var(--chart-grid)",
					}}
				>
					{yTicks.map((belgi) => (
						<div
							key={belgi.value}
							data-y-tick
							aria-hidden="true"
							className="pointer-events-none absolute inset-x-0"
							style={{
								bottom: `${joy(belgi.value, yMin, yMax)}%`,
								borderTop: "1px solid var(--chart-grid)",
							}}
						/>
					))}

					{quadrant ? <Kvadrant quadrant={quadrant} {...{ xMin, xMax, yMin, yMax }} /> : null}

					{points.map((nuqta) => (
						<div
							key={nuqta.key}
							data-point
							title={nuqta.tooltip}
							className="absolute h-2.5 w-2.5 -translate-x-1/2 translate-y-1/2 rounded-full"
							style={{
								left: `${joy(nuqta.x, xMin, xMax)}%`,
								bottom: `${joy(nuqta.y, yMin, yMax)}%`,
								backgroundColor: nuqta.color,
								boxShadow: "0 0 0 2px var(--chart-surface)",
							}}
						>
							{nuqta.label ? (
								<span
									data-point-label
									// ⚠️ O'ng chetda (> 70%) yorliq chapga ochiladi — aks holda
									// chizma chegarasidan chiqib kesilardi.
									className={`absolute bottom-full whitespace-nowrap text-[10px] ${
										joy(nuqta.x, xMin, xMax) > 70 ? "right-full mr-0.5" : "left-full ml-0.5"
									}`}
									style={{ color: "var(--chart-ink)" }}
								>
									{nuqta.label}
								</span>
							) : null}
						</div>
					))}
				</div>
			</div>

			<div className="mt-1 pl-8 text-center text-[10px]" style={{ color: "var(--chart-axis)" }}>
				{xLabel}
			</div>
		</div>
	);
}

function Kvadrant({
	quadrant,
	xMin,
	xMax,
	yMin,
	yMax,
}: {
	quadrant: ScatterQuadrant;
	xMin: number;
	xMax: number;
	yMin: number;
	yMax: number;
}) {
	const x = joy(quadrant.x, xMin, xMax);
	const y = joy(quadrant.y, yMin, yMax);
	const yuqori = quadrant.highlight === "top-right";

	return (
		<div
			data-quadrant
			className="pointer-events-none absolute p-1 text-right text-[10px]"
			style={{
				left: `${x}%`,
				right: 0,
				...(yuqori ? { top: 0, bottom: `${y}%` } : { bottom: 0, height: `${y}%` }),
				backgroundColor: "color-mix(in srgb, var(--chart-kelmagan) 8%, transparent)",
				color: "var(--chart-kelmagan)",
			}}
		>
			{quadrant.label}
		</div>
	);
}
